import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const SettingsPage = () => {
  const { axiosInstance, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState(null);
  const [twilioTestPhone, setTwilioTestPhone] = useState('');
  const [twilioTesting, setTwilioTesting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    tax_code: '',
    vat_number: '',
    excise_code: '',
    excise_code_gas: '',
    address: '',
    city: '',
    province: '',
    cap: '',
    email: '',
    test_email: '',
    contracts_email: '',
    practices_email: '',
    contact_phone: '',
    contact_website: '',
    contact_tagline: '',
    pec_email: '',
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_from_number: '',
    twilio_enabled: false,
    sms_provider: 'twilio',
    openapi_token: '',
    openapi_sms_sender: '',
    openapi_credit_check_enabled: false,
    openapi_env: 'production',
    openapi_allow_paid_company: false,
    openapi_allow_paid_sms: false,
    pec_smtp_host: '',
    pec_smtp_port: 465,
    pec_smtp_user: '',
    pec_smtp_password: '',
    pec_use_tls: true,
    mail_smtp_host: '',
    mail_smtp_port: 587,
    mail_smtp_user: '',
    mail_smtp_password: '',
    email_provider: 'smtp',
    sendgrid_api_key: '',
    sendgrid_from_email: '',
    sendgrid_from_name: '',
    mail_smtp_use_tls: true,
    soac_level: null,
    guarantee_amount: 0,
    legal_representative_name: '',
    legal_representative_role: '',
    session_timeout_minutes: 60,
    archive_settings: { fatture_days: 30, leads_days: 90, auto_archive_enabled: true },
  });

  const fetchCompany = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`/companies/${user.company_id}`);
      setCompany(data);
      setFormData({
        name: data.name || '',
        tax_code: data.tax_code || '',
        vat_number: data.vat_number || '',
        excise_code: data.excise_code || '',
        excise_code_gas: data.excise_code_gas || '',
        address: data.address || '',
        city: data.city || '',
        province: data.province || '',
        cap: data.cap || '',
        email: data.email || '',
        test_email: data.test_email || '',
        contracts_email: data.contracts_email || '',
        practices_email: data.practices_email || '',
        contact_phone: data.contact_phone || '',
        contact_website: data.contact_website || '',
        contact_tagline: data.contact_tagline || '',
        pec_email: data.pec_email || '',
        twilio_account_sid: data.twilio_account_sid || '',
        twilio_auth_token: data.twilio_auth_token || '',
        twilio_from_number: data.twilio_from_number || '',
        twilio_enabled: !!data.twilio_enabled,
        sms_provider: data.sms_provider || 'twilio',
        openapi_token: data.openapi_token || '',
        openapi_sms_sender: data.openapi_sms_sender || '',
        openapi_credit_check_enabled: !!data.openapi_credit_check_enabled,
        openapi_env: data.openapi_env || 'production',
        openapi_allow_paid_company: !!data.openapi_allow_paid_company,
        openapi_allow_paid_sms: !!data.openapi_allow_paid_sms,
        pec_smtp_host: data.pec_smtp_host || 'smtps.pec.aruba.it',
        pec_smtp_port: data.pec_smtp_port || 465,
        pec_smtp_user: data.pec_smtp_user || '',
        pec_smtp_password: '',
        pec_use_tls: data.pec_use_tls ?? true,
        mail_smtp_host: data.mail_smtp_host || '',
        mail_smtp_port: data.mail_smtp_port || 587,
        mail_smtp_user: data.mail_smtp_user || '',
        mail_smtp_password: '',
        mail_smtp_use_tls: data.mail_smtp_use_tls ?? true,
        email_provider: data.email_provider || 'smtp',
        sendgrid_api_key: '',  // never echoed back
        sendgrid_api_key_set: !!data.sendgrid_api_key_set,
        sendgrid_from_email: data.sendgrid_from_email || '',
        sendgrid_from_name: data.sendgrid_from_name || '',
        soac_level: data.soac_level,
        guarantee_amount: data.guarantee_amount || 0,
        legal_representative_name: data.legal_representative_name || '',
        legal_representative_role: data.legal_representative_role || '',
        session_timeout_minutes: data.session_timeout_minutes || 60,
        archive_settings: {
          fatture_days: data.archive_settings?.fatture_days ?? 30,
          leads_days: data.archive_settings?.leads_days ?? 90,
          auto_archive_enabled: data.archive_settings?.auto_archive_enabled !== false,
        },
      });
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, user?.company_id]);

  useEffect(() => {
    if (user?.company_id) {
      fetchCompany();
    } else {
      setLoading(false);
    }
  }, [user?.company_id, fetchCompany]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const updateData = { ...formData };
      if (!updateData.pec_smtp_password) {
        delete updateData.pec_smtp_password;
      }
      if (!updateData.mail_smtp_password) {
        delete updateData.mail_smtp_password;
      }
      // SendGrid API key: don't blank out if user left field empty
      if (!updateData.sendgrid_api_key) {
        delete updateData.sendgrid_api_key;
      }
      delete updateData.sendgrid_api_key_set;

      // Clamp session timeout 5..1440 min before sending
      if (updateData.session_timeout_minutes) {
        const v = parseInt(updateData.session_timeout_minutes, 10) || 60;
        updateData.session_timeout_minutes = Math.max(5, Math.min(1440, v));
      }

      await axiosInstance.put(`/companies/${user.company_id}`, updateData);
      toast.success('Impostazioni salvate con successo!');
      fetchCompany();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleTwilioTest = async () => {
    if (!twilioTestPhone) return;
    setTwilioTesting(true);
    try {
      const { data } = await axiosInstance.post('/companies/twilio-test', { phone: twilioTestPhone });
      if (data.success) {
        toast.success(`SMS inviato a ${data.phone}`, { duration: 6000 });
      } else {
        const det = data.details || {};
        const msg = det.hint || det.error || 'SMS non inviato';
        toast.error(msg, { duration: 12000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore Twilio test');
    } finally {
      setTwilioTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#0A2540] border-r-transparent"></div>
          <p className="mt-4 text-sm text-slate-600">Caricamento impostazioni...</p>
        </div>
      </div>
    );
  }

  if (!user?.company_id) {
    return (
      <div className="max-w-2xl">
        <h1 className="text-4xl font-extrabold tracking-tighter text-slate-900 mb-6">Impostazioni Aziendali</h1>
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-6">
          <p className="text-sm text-amber-900">Devi essere associato a un'azienda per modificare le impostazioni.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h1 className="text-4xl font-extrabold tracking-tighter text-slate-900">Impostazioni Aziendali</h1>
        <p className="mt-2 text-sm text-slate-600">Configurazione dati azienda, PEC, SOAC e cauzione</p>
      </div>

      <form onSubmit={handleSave} className="space-y-8">
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-4">Informazioni Generali</h2>

          <div className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-sm font-semibold text-slate-700">Nome Azienda *</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-2 bg-white border-slate-300 rounded-sm"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="vat_number" className="text-sm font-semibold text-slate-700">Partita IVA *</Label>
                <Input
                  id="vat_number"
                  type="text"
                  value={formData.vat_number}
                  onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                  className="mt-2 bg-white border-slate-300 rounded-sm font-mono"
                  placeholder="18194711000"
                  data-testid="company-vat-input"
                />
              </div>
              <div>
                <Label htmlFor="tax_code" className="text-sm font-semibold text-slate-700">Codice Fiscale</Label>
                <Input
                  id="tax_code"
                  type="text"
                  value={formData.tax_code}
                  onChange={(e) => setFormData({ ...formData, tax_code: e.target.value })}
                  className="mt-2 bg-white border-slate-300 rounded-sm font-mono"
                  placeholder="Se diverso dalla P.IVA"
                  data-testid="company-taxcode-input"
                />
                <p className="mt-1 text-xs text-slate-600">Lasciare vuoto se uguale alla P.IVA</p>
              </div>
            </div>

            <div>
              <Label htmlFor="excise_code" className="text-sm font-semibold text-slate-700">
                Codice Ditta Energia * <span className="text-xs text-slate-500">(Provincia + ENERGIA, es: RM_ENERGIA_123456)</span>
              </Label>
              <Input
                id="excise_code"
                type="text"
                value={formData.excise_code}
                onChange={(e) => setFormData({ ...formData, excise_code: e.target.value })}
                className="mt-2 bg-white border-slate-300 rounded-sm font-mono"
                placeholder="RM_ENERGIA_"
                required
              />
              <p className="mt-1 text-xs text-slate-600">Inserisci il codice ditta ENERGIA assegnato dalle Dogane</p>
            </div>

            <div>
              <Label htmlFor="excise_code_gas" className="text-sm font-semibold text-slate-700">
                Codice Ditta GAS <span className="text-xs text-slate-500">(Provincia + GAS, es: RM_GAS_123456)</span>
              </Label>
              <Input
                id="excise_code_gas"
                type="text"
                value={formData.excise_code_gas}
                onChange={(e) => setFormData({ ...formData, excise_code_gas: e.target.value })}
                className="mt-2 bg-white border-slate-300 rounded-sm font-mono"
                placeholder="RM_GAS_"
              />
              <p className="mt-1 text-xs text-slate-600">Inserisci il codice ditta GAS assegnato dalle Dogane (opzionale)</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city" className="text-sm font-semibold text-slate-700">Città</Label>
                <Input
                  id="city"
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="mt-2 bg-white border-slate-300 rounded-sm"
                />
              </div>
              <div>
                <Label htmlFor="province" className="text-sm font-semibold text-slate-700">Provincia</Label>
                <Input
                  id="province"
                  type="text"
                  value={formData.province}
                  onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                  className="mt-2 bg-white border-slate-300 rounded-sm"
                  maxLength={2}
                  placeholder="RM"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="address" className="text-sm font-semibold text-slate-700">Indirizzo</Label>
              <Input
                id="address"
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="mt-2 bg-white border-slate-300 rounded-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email" className="text-sm font-semibold text-slate-700">Email Aziendale</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-2 bg-white border-slate-300 rounded-sm"
                  placeholder="info@azienda.it"
                  data-testid="company-email-input"
                />
              </div>
              <div>
                <Label htmlFor="test_email" className="text-sm font-semibold text-slate-700">Email di Test</Label>
                <Input
                  id="test_email"
                  type="email"
                  value={formData.test_email}
                  onChange={(e) => setFormData({ ...formData, test_email: e.target.value })}
                  className="mt-2 bg-white border-slate-300 rounded-sm"
                  placeholder="info@orange-energy.it"
                  data-testid="test-email-input"
                />
                <p className="mt-1 text-xs text-slate-600">Per invio mail di test prima dell'invio PEC definitivo</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sprint 5.3 — Comunicazioni & Contatti centralizzati (Email Contratti, Pratiche, Footer) */}
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-1">Comunicazioni & Contatti</h2>
          <p className="text-sm text-slate-500 mb-4">
            Indirizzi email dedicati + dati di contatto che appariranno nella firma di tutte le email e documenti generati.
            Pescati automaticamente da software quando cambi azienda.
          </p>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="contracts_email" className="text-sm font-semibold text-slate-700">
                📬 Email Invio Contratti
              </Label>
              <Input
                id="contracts_email"
                type="email"
                value={formData.contracts_email || ''}
                onChange={(e) => setFormData({ ...formData, contracts_email: e.target.value })}
                className="mt-2 bg-white border-slate-300 rounded-sm"
                placeholder="contratti@azienda.it"
                data-testid="contracts-email-input"
              />
              <p className="mt-1 text-xs text-slate-600">
                Reply-To e CC automatico di ogni invio pre-contratto. Qui arrivano le scansioni firmate.
              </p>
            </div>
            <div>
              <Label htmlFor="practices_email" className="text-sm font-semibold text-slate-700">
                📋 Email Invio Pratiche
              </Label>
              <Input
                id="practices_email"
                type="email"
                value={formData.practices_email || ''}
                onChange={(e) => setFormData({ ...formData, practices_email: e.target.value })}
                className="mt-2 bg-white border-slate-300 rounded-sm"
                placeholder="pratiche@azienda.it"
                data-testid="practices-email-input"
              />
              <p className="mt-1 text-xs text-slate-600">
                Reply-To per dichiarazioni ADM / Dogane / comunicazioni istituzionali.
              </p>
            </div>
          </div>

          <div className="mt-6 border-t border-slate-200 pt-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Contatti footer email & PDF</h3>
            <p className="text-xs text-slate-500 mb-3">
              Questi dati appariranno nella firma email e in tutti i documenti generati dal software (contratti, F24, dichiarazioni). Cambiando qui, cambia ovunque.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_phone" className="text-sm font-semibold text-slate-700">Telefono</Label>
                <Input
                  id="contact_phone"
                  type="text"
                  value={formData.contact_phone || ''}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  className="mt-2 bg-white border-slate-300 rounded-sm"
                  placeholder="+39 06 12345678"
                  data-testid="contact-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="contact_website" className="text-sm font-semibold text-slate-700">Sito Web</Label>
                <Input
                  id="contact_website"
                  type="text"
                  value={formData.contact_website || ''}
                  onChange={(e) => setFormData({ ...formData, contact_website: e.target.value })}
                  className="mt-2 bg-white border-slate-300 rounded-sm"
                  placeholder="www.azienda.it"
                  data-testid="contact-website-input"
                />
              </div>
            </div>
            <div className="mt-4">
              <Label htmlFor="contact_tagline" className="text-sm font-semibold text-slate-700">Tagline / Payoff</Label>
              <Input
                id="contact_tagline"
                type="text"
                value={formData.contact_tagline || ''}
                onChange={(e) => setFormData({ ...formData, contact_tagline: e.target.value })}
                className="mt-2 bg-white border-slate-300 rounded-sm"
                placeholder="Energia certificata verde per le aziende italiane"
                data-testid="contact-tagline-input"
              />
              <p className="mt-1 text-xs text-slate-600">Breve claim visualizzato sotto il logo nella firma email.</p>
            </div>
          </div>
        </div>

        {/* Sprint 5.4 — Twilio SMS per OTP firma */}
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-1">SMS Twilio (OTP firma)</h2>
          <p className="text-sm text-slate-500 mb-4">
            Configura Twilio per inviare il codice OTP via SMS al cliente come alternativa alla firma email.
            <a href="https://console.twilio.com" target="_blank" rel="noreferrer" className="text-orange-600 hover:underline ml-1">Apri Twilio Console ↗</a>
          </p>

          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={!!formData.twilio_enabled}
                     onChange={(e) => setFormData({ ...formData, twilio_enabled: e.target.checked })}
                     data-testid="twilio-enabled" />
              <span className="text-sm font-semibold text-slate-700">Abilita SMS via Twilio</span>
            </label>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="twilio_account_sid" className="text-sm font-semibold text-slate-700">Account SID</Label>
                <Input id="twilio_account_sid" type="text"
                       value={formData.twilio_account_sid || ''}
                       onChange={(e) => setFormData({ ...formData, twilio_account_sid: e.target.value })}
                       className="mt-2 bg-white border-slate-300 rounded-sm font-mono text-xs"
                       placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                       data-testid="twilio-sid-input" />
                <p className="mt-1 text-xs text-slate-600">Lo trovi nella home della Twilio Console.</p>
              </div>
              <div>
                <Label htmlFor="twilio_auth_token" className="text-sm font-semibold text-slate-700">Auth Token</Label>
                <Input id="twilio_auth_token" type="password"
                       value={formData.twilio_auth_token || ''}
                       onChange={(e) => setFormData({ ...formData, twilio_auth_token: e.target.value })}
                       className="mt-2 bg-white border-slate-300 rounded-sm font-mono text-xs"
                       placeholder="••••••••••••••••••••••••••••••••"
                       data-testid="twilio-token-input" />
                <p className="mt-1 text-xs text-slate-600">Cifrato e mai mostrato dopo il salvataggio.</p>
              </div>
            </div>
            <div>
              <Label htmlFor="twilio_from_number" className="text-sm font-semibold text-slate-700">Numero mittente Twilio</Label>
              <Input id="twilio_from_number" type="text"
                     value={formData.twilio_from_number || ''}
                     onChange={(e) => setFormData({ ...formData, twilio_from_number: e.target.value })}
                     className="mt-2 bg-white border-slate-300 rounded-sm font-mono"
                     placeholder="+15551234567 (numero Twilio acquistato)"
                     data-testid="twilio-from-input" />
              <p className="mt-1 text-xs text-slate-600">
                Numero Twilio acquistato in formato E.164 (+...). 
                <strong>Non usare</strong> il tuo cellulare personale o un Sender ID alfanumerico — non funzionano sui trial.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900 space-y-1">
              <div><strong>⚠ Trial Twilio — limitazioni:</strong></div>
              <div>1️⃣ <strong>Numero mittente</strong>: usa il <strong>numero Twilio gratis</strong> assegnato al trial — lo trovi su <em>Phone Numbers → Manage → Active numbers</em>. Gli <strong>Alphanumeric Sender ID</strong> (es. "OrangeEnergy") <strong>NON funzionano sui trial</strong>, richiedono account a pagamento + registrazione.</div>
              <div>2️⃣ <strong>Destinatari</strong>: invii SMS solo a numeri aggiunti come "Verified Caller IDs" su Twilio Console (anche il tuo cellulare di test).</div>
              <div>3️⃣ I "Verified Caller IDs" servono per <strong>ricevere</strong> SMS, NON per essere mittenti. Mittente = numero comprato da Twilio.</div>
            </div>

            <div className="border-t pt-4">
              <Label htmlFor="twilio_test_phone" className="text-sm font-semibold text-slate-700">Test invio SMS</Label>
              <div className="flex gap-2 mt-2">
                <Input id="twilio_test_phone" type="text"
                       placeholder="+393331234567 (numero verificato)"
                       value={twilioTestPhone}
                       onChange={(e) => setTwilioTestPhone(e.target.value)}
                       className="bg-white border-slate-300 rounded-sm font-mono text-sm"
                       data-testid="twilio-test-phone-input" />
                <button type="button" onClick={handleTwilioTest}
                        disabled={twilioTesting || !twilioTestPhone}
                        className="px-4 py-2 bg-slate-900 text-white text-sm rounded hover:bg-slate-800 disabled:opacity-50"
                        data-testid="twilio-test-send-btn">
                  {twilioTesting ? 'Invio...' : 'Invia SMS test'}
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-600">
                Salva prima le impostazioni, poi invia un SMS al tuo numero per verificare le credenziali.
              </p>
            </div>
          </div>
        </div>

        {/* Sprint 6 — Openapi.it: Credit Check + SMS multi-provider (3 tab) */}
        <OpenapiSettingsCard formData={formData} setFormData={setFormData} />

        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-4">Rappresentante Legale</h2>

          <div className="space-y-4">
            <div>
              <Label htmlFor="legal_rep_name" className="text-sm font-semibold text-slate-700">Nome e Cognome</Label>
              <Input
                id="legal_rep_name"
                type="text"
                value={formData.legal_representative_name}
                onChange={(e) => setFormData({ ...formData, legal_representative_name: e.target.value })}
                className="mt-2 bg-white border-slate-300 rounded-sm"
                placeholder="Mario Rossi"
              />
            </div>
            <div>
              <Label htmlFor="legal_rep_role" className="text-sm font-semibold text-slate-700">Ruolo</Label>
              <Input
                id="legal_rep_role"
                type="text"
                value={formData.legal_representative_role}
                onChange={(e) => setFormData({ ...formData, legal_representative_role: e.target.value })}
                className="mt-2 bg-white border-slate-300 rounded-sm"
                placeholder="Amministratore Unico"
              />
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="security-session-card">
          <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-1">Sicurezza & Sessione</h2>
          <p className="text-xs text-slate-500 mb-4">
            Tempo di validità della sessione di un utente loggato. Quando l'access token scade,
            il sistema lo rinnova automaticamente in background fino alla scadenza del refresh
            (7 giorni). Logout manuale o cambio password invalidano subito la sessione.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="session_timeout_minutes" className="text-sm font-semibold text-slate-700">
                Durata access token (minuti) <span className="text-slate-400 font-normal">— da 5 a 1440</span>
              </Label>
              <Input
                id="session_timeout_minutes"
                type="number"
                min={5}
                max={1440}
                step={5}
                value={formData.session_timeout_minutes}
                onChange={(e) => setFormData({ ...formData, session_timeout_minutes: parseInt(e.target.value || '60', 10) })}
                className="mt-2 bg-white border-slate-300 rounded-sm font-mono"
                data-testid="input-session-timeout"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Default 60 min. Valori suggeriti: 30 (alta sicurezza), 60 (standard), 240 (uffici fissi), 480 (8h work-shift).
              </p>
            </div>
            <div className="flex flex-wrap gap-2 items-end pb-1">
              {[30, 60, 240, 480].map(v => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setFormData({ ...formData, session_timeout_minutes: v })}
                  className={`px-3 py-1.5 rounded text-xs font-medium border transition ${
                    formData.session_timeout_minutes === v
                      ? 'bg-orange-500 text-white border-orange-500'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                  data-testid={`session-preset-${v}`}>
                  {v < 60 ? `${v} min` : `${v / 60}h`}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="archive-policies-card">
          <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-1">Politiche di archiviazione</h2>
          <p className="text-xs text-slate-500 mb-4">
            Le fatture analizzate e i lead inattivi vengono archiviati automaticamente ogni notte (02:30).
            L&apos;archivio è sempre <strong>ripristinabile</strong>: nessuna cancellazione dati. I lead con contratti
            attivi o in attivazione sono <strong>protetti</strong> e non vengono mai archiviati.
          </p>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="archive_fatture_days" className="text-sm font-semibold text-slate-700">
                Fatture analizzate · giorni
              </Label>
              <Input
                id="archive_fatture_days"
                type="number"
                min={7}
                max={365}
                value={formData.archive_settings.fatture_days}
                onChange={(e) => setFormData({
                  ...formData,
                  archive_settings: { ...formData.archive_settings, fatture_days: parseInt(e.target.value || '30', 10) },
                })}
                className="mt-2 bg-white border-slate-300 rounded-sm font-mono"
                data-testid="input-archive-fatture-days"
              />
              <p className="text-[11px] text-slate-500 mt-1">Default 30g. Min 7, max 365.</p>
            </div>
            <div>
              <Label htmlFor="archive_leads_days" className="text-sm font-semibold text-slate-700">
                Lead inattivi · giorni
              </Label>
              <Input
                id="archive_leads_days"
                type="number"
                min={30}
                max={730}
                value={formData.archive_settings.leads_days}
                onChange={(e) => setFormData({
                  ...formData,
                  archive_settings: { ...formData.archive_settings, leads_days: parseInt(e.target.value || '90', 10) },
                })}
                className="mt-2 bg-white border-slate-300 rounded-sm font-mono"
                data-testid="input-archive-leads-days"
              />
              <p className="text-[11px] text-slate-500 mt-1">Default 90g. Esclude lead con contratti attivi/in attivazione.</p>
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="toggle-auto-archive">
                <input
                  type="checkbox"
                  checked={formData.archive_settings.auto_archive_enabled}
                  onChange={(e) => setFormData({
                    ...formData,
                    archive_settings: { ...formData.archive_settings, auto_archive_enabled: e.target.checked },
                  })}
                  className="w-4 h-4 accent-orange-500"
                />
                <span className="font-medium">Archiviazione automatica attiva</span>
              </label>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-4">Configurazione PEC</h2>

          <div className="space-y-4">
            <div>
              <Label htmlFor="pec_email" className="text-sm font-semibold text-slate-700">Email PEC</Label>
              <Input
                id="pec_email"
                type="email"
                value={formData.pec_email}
                onChange={(e) => setFormData({ ...formData, pec_email: e.target.value })}
                className="mt-2 bg-white border-slate-300 rounded-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pec_smtp_host" className="text-sm font-semibold text-slate-700">Host SMTP</Label>
                <Input
                  id="pec_smtp_host"
                  type="text"
                  value={formData.pec_smtp_host}
                  onChange={(e) => setFormData({ ...formData, pec_smtp_host: e.target.value })}
                  className="mt-2 bg-white border-slate-300 rounded-sm"
                  placeholder="smtps.pec.aruba.it"
                />
              </div>
              <div>
                <Label htmlFor="pec_smtp_port" className="text-sm font-semibold text-slate-700">Porta SMTP</Label>
                <Input
                  id="pec_smtp_port"
                  type="number"
                  value={formData.pec_smtp_port}
                  onChange={(e) => setFormData({ ...formData, pec_smtp_port: parseInt(e.target.value) })}
                  className="mt-2 bg-white border-slate-300 rounded-sm"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="pec_smtp_user" className="text-sm font-semibold text-slate-700">Username SMTP</Label>
              <Input
                id="pec_smtp_user"
                type="text"
                value={formData.pec_smtp_user}
                onChange={(e) => setFormData({ ...formData, pec_smtp_user: e.target.value })}
                className="mt-2 bg-white border-slate-300 rounded-sm"
              />
            </div>

            <div>
              <Label htmlFor="pec_smtp_password" className="text-sm font-semibold text-slate-700">Password SMTP</Label>
              <Input
                id="pec_smtp_password"
                type="password"
                value={formData.pec_smtp_password}
                onChange={(e) => setFormData({ ...formData, pec_smtp_password: e.target.value })}
                className="mt-2 bg-white border-slate-300 rounded-sm"
                placeholder="Lascia vuoto per non modificare"
              />
            </div>
          </div>
        </div>

        <EmailProviderCard formData={formData} setFormData={setFormData} />

        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-4">Configurazione Mail Ordinaria</h2>
          <p className="text-xs text-slate-500 mb-4">SMTP usato quando "SMTP Aruba" è selezionato come provider sopra. Se hai scelto SendGrid puoi lasciare questi campi vuoti.</p>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="mail_smtp_host" className="text-sm font-semibold text-slate-700">Host SMTP</Label>
                <Input
                  id="mail_smtp_host"
                  type="text"
                  value={formData.mail_smtp_host}
                  onChange={(e) => setFormData({ ...formData, mail_smtp_host: e.target.value })}
                  className="mt-2 bg-white border-slate-300 rounded-sm"
                  placeholder="smtp.gmail.com"
                  data-testid="mail-smtp-host"
                />
              </div>
              <div>
                <Label htmlFor="mail_smtp_port" className="text-sm font-semibold text-slate-700">Porta</Label>
                <Input
                  id="mail_smtp_port"
                  type="number"
                  value={formData.mail_smtp_port}
                  onChange={(e) => setFormData({ ...formData, mail_smtp_port: parseInt(e.target.value) || 587 })}
                  className="mt-2 bg-white border-slate-300 rounded-sm"
                  data-testid="mail-smtp-port"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="mail_smtp_user" className="text-sm font-semibold text-slate-700">Username SMTP</Label>
              <Input
                id="mail_smtp_user"
                type="text"
                value={formData.mail_smtp_user}
                onChange={(e) => setFormData({ ...formData, mail_smtp_user: e.target.value })}
                className="mt-2 bg-white border-slate-300 rounded-sm"
                placeholder="utente@gmail.com"
                data-testid="mail-smtp-user"
              />
            </div>
            <div>
              <Label htmlFor="mail_smtp_password" className="text-sm font-semibold text-slate-700">Password SMTP</Label>
              <Input
                id="mail_smtp_password"
                type="password"
                value={formData.mail_smtp_password}
                onChange={(e) => setFormData({ ...formData, mail_smtp_password: e.target.value })}
                className="mt-2 bg-white border-slate-300 rounded-sm"
                placeholder="Lascia vuoto per non modificare"
                data-testid="mail-smtp-password"
              />
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-4">Livello SOAC</h2>

          <div>
            <Label htmlFor="soac_level" className="text-sm font-semibold text-slate-700">Livello SOAC Aziendale</Label>
            <Select
              value={formData.soac_level || 'null'}
              onValueChange={(value) => setFormData({ ...formData, soac_level: value === 'null' ? null : value })}
            >
              <SelectTrigger className="mt-2 bg-white border-slate-300 rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="null">Non Accreditato</SelectItem>
                <SelectItem value="Base">Base (-30% cauzione)</SelectItem>
                <SelectItem value="Medio">Medio (-50% cauzione)</SelectItem>
                <SelectItem value="Avanzato">Avanzato (esenzione cauzione)</SelectItem>
              </SelectContent>
            </Select>
            <p className="mt-2 text-xs text-slate-600">Il livello SOAC determina riduzioni sulla cauzione e workflow operativi</p>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-4">Gestione Cauzione</h2>

          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-sm">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-blue-700 mb-1">Cauzione Versata</p>
                  <p className="text-2xl font-bold text-blue-900">
                    €{(formData.guarantee_amount || 0).toLocaleString('it-IT')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-blue-700 mb-1">Cauzione Dovuta (calcolata)</p>
                  <p className="text-2xl font-bold text-blue-900">
                    €{(company?.guarantee_required || 0).toLocaleString('it-IT')}
                  </p>
                </div>
              </div>
              {company?.guarantee_delta !== undefined && (
                <div className="mt-3 pt-3 border-t border-blue-300">
                  <p className="text-xs text-blue-700">
                    Delta: <strong className={company.guarantee_delta >= 0 ? 'text-green-700' : 'text-red-700'}>
                      {company.guarantee_delta >= 0 ? '+' : ''}€{company.guarantee_delta.toLocaleString('it-IT')}
                    </strong>
                  </p>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="guarantee_amount" className="text-sm font-semibold text-slate-700">Cauzione Versata (€)</Label>
              <Input
                id="guarantee_amount"
                type="number"
                step="0.01"
                value={formData.guarantee_amount}
                onChange={(e) => setFormData({ ...formData, guarantee_amount: parseFloat(e.target.value) })}
                className="mt-2 bg-white border-slate-300 rounded-sm"
              />
              <p className="mt-1 text-xs text-slate-600">Aggiorna questo campo quando effettui versamenti alle Dogane</p>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            type="submit"
            disabled={saving}
            className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-sm"
          >
            {saving ? 'Salvataggio...' : 'Salva Impostazioni'}
          </Button>
        </div>
      </form>
    </div>
  );
};

// --- Openapi.it Settings Card with 3 tabs (Token/Env, Company, SMS) ---
const OpenapiSettingsCard = ({ formData, setFormData }) => {
  const [tab, setTab] = useState('token');

  const tabBtn = (id, label) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      data-testid={`openapi-tab-${id}`}
      className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
        tab === id
          ? 'border-orange-500 text-orange-600'
          : 'border-transparent text-slate-500 hover:text-slate-800'
      }`}>
      {label}
    </button>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="openapi-settings-card">
      <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-1">Openapi.it</h2>
      <p className="text-sm text-slate-500 mb-4">
        Token unico per servizi Italia: <strong>Company</strong> (verifica P.IVA + bilanci),
        <strong> SMS v2</strong> (mittente alfanumerico). Le funzioni a pagamento sono OFF di default.
        <a href="https://console.openapi.com" target="_blank" rel="noreferrer"
           className="text-orange-600 hover:underline ml-1">Apri Console Openapi ↗</a>
      </p>

      {/* Tabs */}
      <div className="flex border-b mb-4">
        {tabBtn('token', 'Token & Ambiente')}
        {tabBtn('company', 'Company API')}
        {tabBtn('sms', 'SMS v2')}
      </div>

      {/* Tab: Token & Ambiente */}
      {tab === 'token' && (
        <div className="space-y-4" data-testid="openapi-tabpanel-token">
          <div>
            <Label htmlFor="openapi_token" className="text-sm font-semibold text-slate-700">Token API (Bearer)</Label>
            <Input id="openapi_token" type="password"
                   value={formData.openapi_token || ''}
                   onChange={(e) => setFormData({ ...formData, openapi_token: e.target.value })}
                   className="mt-2 bg-white border-slate-300 rounded-sm font-mono text-xs"
                   placeholder="••••••••••••••••••••••••"
                   data-testid="openapi-token-input" />
            <p className="mt-1 text-xs text-slate-600">
              Console Openapi → <strong>Autenticazione</strong> → "+Nuovo Token" autorizzando IT-start, IT-full, SMS Italia.
            </p>
          </div>
          <div>
            <Label className="text-sm font-semibold text-slate-700">Ambiente Openapi</Label>
            <select value={formData.openapi_env || 'production'}
                    onChange={(e) => setFormData({ ...formData, openapi_env: e.target.value })}
                    className="mt-2 w-full text-sm border rounded-sm px-3 py-2 bg-white"
                    data-testid="openapi-env-select">
              <option value="production">Production (company.openapi.com / ws.messaggisms.com)</option>
              <option value="test">Sandbox (test.company.openapi.com / test.ws.messaggisms.com)</option>
            </select>
            <p className="mt-1 text-xs text-slate-600">
              Usa "Production" se il profilo è verificato. "Sandbox" per test gratuiti.
            </p>
          </div>
        </div>
      )}

      {/* Tab: Company API */}
      {tab === 'company' && (
        <div className="space-y-4" data-testid="openapi-tabpanel-company">
          <p className="text-sm text-slate-600">
            Scegli la modalità Company API. Cambierà sia il comportamento del bottone "Verifica solvibilità"
            sui lead, sia il gate automatico al passaggio lead → pre-contratto.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Modalità A — Free */}
            <label
              data-testid="openapi-mode-free"
              className={`cursor-pointer rounded-lg border-2 p-4 transition ${
                !formData.openapi_allow_paid_company
                  ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}>
              <div className="flex items-start gap-3">
                <input
                  type="radio"
                  name="openapi_company_mode"
                  className="mt-1 accent-emerald-600"
                  checked={!formData.openapi_allow_paid_company}
                  onChange={() => setFormData({ ...formData, openapi_allow_paid_company: false })}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-700 text-xl leading-none">🆓</span>
                    <h3 className="font-bold text-emerald-900">Solo info gratuite</h3>
                  </div>
                  <p className="text-xs text-emerald-800 mt-2">
                    Chiamata <strong>IT-start</strong> per:
                  </p>
                  <ul className="text-xs text-emerald-800 mt-1 ml-4 list-disc space-y-0.5">
                    <li>Verifica esistenza P.IVA</li>
                    <li>Autocompila Ragione Sociale, sede, ATECO</li>
                    <li>Blocca aziende CESSATE / FALLITE</li>
                  </ul>
                  <p className="text-xs font-semibold text-emerald-900 mt-2">Costo: gratuito o trascurabile</p>
                  <p className="text-[11px] text-emerald-700 mt-1 italic">Nessun calcolo score: lo stato di solvibilità rimane "manager_review" (verifica manuale).</p>
                </div>
              </div>
            </label>

            {/* Modalità B — Paid */}
            <label
              data-testid="openapi-mode-paid"
              className={`cursor-pointer rounded-lg border-2 p-4 transition ${
                formData.openapi_allow_paid_company
                  ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-200'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}>
              <div className="flex items-start gap-3">
                <input
                  type="radio"
                  name="openapi_company_mode"
                  className="mt-1 accent-orange-600"
                  checked={!!formData.openapi_allow_paid_company}
                  onChange={() => setFormData({ ...formData, openapi_allow_paid_company: true })}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-orange-700 text-xl leading-none">💰</span>
                    <h3 className="font-bold text-orange-900">Credit Check a pagamento</h3>
                  </div>
                  <p className="text-xs text-orange-800 mt-2">
                    Tutto il gratuito + chiamata <strong>IT-full</strong> per:
                  </p>
                  <ul className="text-xs text-orange-800 mt-1 ml-4 list-disc space-y-0.5">
                    <li>Bilancio (fatturato, patrimonio netto, ROE, EBITDA)</li>
                    <li>Numero dipendenti e trend</li>
                    <li>Score solvibilità 0-100 (Cerved-equivalent)</li>
                    <li>Blocco automatico score &lt; 30</li>
                  </ul>
                  <p className="text-xs font-semibold text-orange-900 mt-2">Costo: ~€0,089-0,30 per richiesta</p>
                  <p className="text-[11px] text-orange-700 mt-1 italic">Cache 24h: stessa P.IVA chiamata max 1 volta al giorno.</p>
                </div>
              </div>
            </label>
          </div>

          <label className="flex items-center gap-3 cursor-pointer pt-3 border-t mt-3">
            <input type="checkbox" checked={!!formData.openapi_credit_check_enabled}
                   onChange={(e) => setFormData({ ...formData, openapi_credit_check_enabled: e.target.checked })}
                   data-testid="openapi-credit-check-enabled" />
            <span className="text-sm font-semibold text-slate-700">
              Esegui automaticamente il check al passaggio lead → pre-contratto
            </span>
          </label>
          <p className="text-xs text-slate-600 -mt-2 ml-7">
            Se attivo, ogni volta che generi un PDF contratto da un lead viene fatto il check (rispettando la modalità sopra).
            Se disattivo, il check parte solo manualmente dai bottoni nel dettaglio lead.
          </p>
        </div>
      )}

      {/* Tab: SMS v2 — 2 card radio (Email gratis / SMS pagamento) */}
      {tab === 'sms' && (
        <div className="space-y-4" data-testid="openapi-tabpanel-sms">
          <p className="text-sm text-slate-600">
            Scegli come il cliente può firmare il contratto via OTP. L'email è sempre disponibile e gratuita;
            l'SMS richiede un provider a pagamento.
          </p>

          {(() => {
            const smsActive = (formData.sms_provider === 'openapi' && formData.openapi_allow_paid_sms) ||
                              (formData.sms_provider === 'twilio' && formData.twilio_enabled);
            const setEmailOnly = () => setFormData({
              ...formData,
              openapi_allow_paid_sms: false,
              twilio_enabled: false,
            });
            const setSmsPaid = () => setFormData({
              ...formData,
              // se l'utente non ha mai impostato un provider, default a Openapi
              sms_provider: formData.sms_provider || 'openapi',
              openapi_allow_paid_sms: (formData.sms_provider || 'openapi') === 'openapi',
              twilio_enabled: formData.sms_provider === 'twilio',
            });
            return (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Modalità A — Email Only */}
                  <label
                    data-testid="otp-mode-email"
                    className={`cursor-pointer rounded-lg border-2 p-4 transition ${
                      !smsActive
                        ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}>
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="otp_mode"
                        className="mt-1 accent-emerald-600"
                        checked={!smsActive}
                        onChange={setEmailOnly}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-emerald-700 text-xl leading-none">🆓</span>
                          <h3 className="font-bold text-emerald-900">Solo Email OTP</h3>
                        </div>
                        <p className="text-xs text-emerald-800 mt-2">
                          Il codice OTP arriva via email al firmatario tramite SMTP Aruba (già configurato).
                        </p>
                        <p className="text-xs font-semibold text-emerald-900 mt-2">Costo: gratuito (incluso SMTP)</p>
                        <p className="text-[11px] text-emerald-700 mt-1 italic">
                          Sulla pagina di firma il bottone "OTP via SMS" non sarà visibile.
                        </p>
                      </div>
                    </div>
                  </label>

                  {/* Modalità B — Email + SMS paid */}
                  <label
                    data-testid="otp-mode-sms"
                    className={`cursor-pointer rounded-lg border-2 p-4 transition ${
                      smsActive
                        ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-200'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}>
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="otp_mode"
                        className="mt-1 accent-orange-600"
                        checked={smsActive}
                        onChange={setSmsPaid}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-orange-700 text-xl leading-none">💰</span>
                          <h3 className="font-bold text-orange-900">Email + SMS a pagamento</h3>
                        </div>
                        <p className="text-xs text-orange-800 mt-2">
                          Tutto il gratuito + opzione SMS sul cellulare del firmatario via Openapi o Twilio.
                        </p>
                        <p className="text-xs font-semibold text-orange-900 mt-2">Costo: ~€0,04-0,08 per SMS</p>
                        <p className="text-[11px] text-orange-700 mt-1 italic">
                          Apre i campi sotto per configurare il provider e il sender ID.
                        </p>
                      </div>
                    </div>
                  </label>
                </div>

                {/* Configurazione provider — visibile solo se modalità SMS attiva */}
                {smsActive && (
                  <div className="border-t pt-4 mt-2 space-y-4" data-testid="sms-config-block">
                    <div>
                      <Label className="text-sm font-semibold text-slate-700">Provider SMS attivo</Label>
                      <select value={formData.sms_provider || 'openapi'}
                              onChange={(e) => {
                                const p = e.target.value;
                                setFormData({
                                  ...formData,
                                  sms_provider: p,
                                  openapi_allow_paid_sms: p === 'openapi',
                                  twilio_enabled: p === 'twilio',
                                });
                              }}
                              className="mt-2 w-full text-sm border rounded-sm px-3 py-2 bg-white"
                              data-testid="sms-provider-select">
                        <option value="openapi">Openapi.it SMS v2 (mittente alfanumerico, no acquisto numero)</option>
                        <option value="twilio">Twilio (numero acquistato)</option>
                      </select>
                      <p className="mt-1 text-xs text-slate-600">
                        Cambiare provider attiva/disattiva automaticamente il flag a pagamento corrispondente.
                      </p>
                    </div>

                    {formData.sms_provider === 'openapi' && (
                      <div>
                        <Label htmlFor="openapi_sms_sender" className="text-sm font-semibold text-slate-700">Sender ID alfanumerico</Label>
                        <Input id="openapi_sms_sender" type="text"
                               value={formData.openapi_sms_sender || ''}
                               onChange={(e) => setFormData({ ...formData, openapi_sms_sender: e.target.value.slice(0, 11) })}
                               className="mt-2 bg-white border-slate-300 rounded-sm font-mono"
                               placeholder="OrangeEnerg"
                               maxLength={11}
                               data-testid="openapi-sender-input" />
                        <p className="mt-1 text-xs text-slate-600">
                          <strong>Max 11 caratteri</strong> alfanumerici + spazi.
                          Es. "OrangeEnerg" o "ORANGE NRG". <strong>Non</strong> "ORANGE ENERGY" (13 char = troppo).
                          Deve essere registrato su Openapi DCRG.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

// --- Email Provider Card with 2 cards (SMTP Aruba / Twilio SendGrid) ---
const EmailProviderCard = ({ formData, setFormData }) => {
  const isSendgrid = (formData.email_provider || 'smtp') === 'sendgrid';

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="email-provider-card">
      <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-1">Provider Email</h2>
      <p className="text-sm text-slate-500 mb-4">
        Scegli come spedire le email transazionali (pre-contratti, OTP via mail, marketing).
        Cambiare provider è istantaneo e non richiede riavvio.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Card A — SMTP Aruba */}
        <label
          data-testid="email-provider-smtp"
          className={`cursor-pointer rounded-lg border-2 p-4 transition ${
            !isSendgrid
              ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200'
              : 'border-slate-200 bg-white hover:border-slate-300'
          }`}>
          <div className="flex items-start gap-3">
            <input
              type="radio"
              name="email_provider"
              className="mt-1 accent-emerald-600"
              checked={!isSendgrid}
              onChange={() => setFormData({ ...formData, email_provider: 'smtp' })}
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-emerald-700 text-xl leading-none">📮</span>
                <h3 className="font-bold text-emerald-900">SMTP Aruba (default)</h3>
              </div>
              <p className="text-xs text-emerald-800 mt-2">
                Invio diretto via SMTP Aruba (smtps.aruba.it / pec.aruba.it). Configurazione tradizionale.
              </p>
              <p className="text-xs font-semibold text-emerald-900 mt-2">Costo: incluso nell'abbonamento Aruba</p>
              <p className="text-[11px] text-emerald-700 mt-1 italic">
                ⚠️ In ambiente di preview/sviluppo gli IP del cluster possono essere blacklistati DNSBL.
                In production (VPS Aruba) funziona senza problemi.
              </p>
            </div>
          </div>
        </label>

        {/* Card B — Twilio SendGrid */}
        <label
          data-testid="email-provider-sendgrid"
          className={`cursor-pointer rounded-lg border-2 p-4 transition ${
            isSendgrid
              ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
              : 'border-slate-200 bg-white hover:border-slate-300'
          }`}>
          <div className="flex items-start gap-3">
            <input
              type="radio"
              name="email_provider"
              className="mt-1 accent-blue-600"
              checked={isSendgrid}
              onChange={() => setFormData({ ...formData, email_provider: 'sendgrid' })}
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-blue-700 text-xl leading-none">🚀</span>
                <h3 className="font-bold text-blue-900">Twilio SendGrid API</h3>
              </div>
              <p className="text-xs text-blue-800 mt-2">
                Invio via API HTTPS (no SMTP). IP dedicati, deliverability eccellente, funziona da qualunque ambiente.
              </p>
              <p className="text-xs font-semibold text-blue-900 mt-2">Costo: 100 mail/giorno gratis (~3.000/mese)</p>
              <p className="text-[11px] text-blue-700 mt-1 italic">
                Richiede mittente verificato in SendGrid (Single Sender Verification).
              </p>
            </div>
          </div>
        </label>
      </div>

      {/* SendGrid config fields */}
      {isSendgrid && (
        <div className="border-t pt-4 mt-4 space-y-4" data-testid="sendgrid-config-block">
          <div>
            <Label htmlFor="sendgrid_api_key" className="text-sm font-semibold text-slate-700">
              SendGrid API Key
            </Label>
            <Input
              id="sendgrid_api_key"
              type="password"
              value={formData.sendgrid_api_key || ''}
              onChange={(e) => setFormData({ ...formData, sendgrid_api_key: e.target.value })}
              placeholder={formData.sendgrid_api_key_set ? '••••••••••••••••••••••• (configurata, lascia vuoto per non modificare)' : 'SG.xxxxxx...'}
              className="mt-2 bg-white border-slate-300 rounded-sm font-mono text-xs"
              data-testid="sendgrid-api-key-input"
              autoComplete="off"
            />
            <p className="mt-1 text-xs text-slate-600">
              SendGrid Console → Settings → API Keys → Create API Key (permission "Mail Send" full).
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="sendgrid_from_email" className="text-sm font-semibold text-slate-700">
                Mittente verificato
              </Label>
              <Input
                id="sendgrid_from_email"
                type="email"
                value={formData.sendgrid_from_email || ''}
                onChange={(e) => setFormData({ ...formData, sendgrid_from_email: e.target.value })}
                placeholder="contratto@orange-energy.it"
                className="mt-2 bg-white border-slate-300 rounded-sm"
                data-testid="sendgrid-from-email-input"
              />
              <p className="mt-1 text-xs text-slate-600">
                Deve essere già verificato in SendGrid → Sender Authentication → Single Sender.
              </p>
            </div>
            <div>
              <Label htmlFor="sendgrid_from_name" className="text-sm font-semibold text-slate-700">
                Nome visualizzato (display name)
              </Label>
              <Input
                id="sendgrid_from_name"
                type="text"
                value={formData.sendgrid_from_name || ''}
                onChange={(e) => setFormData({ ...formData, sendgrid_from_name: e.target.value })}
                placeholder="ENERGIA ARANCIA"
                className="mt-2 bg-white border-slate-300 rounded-sm"
                data-testid="sendgrid-from-name-input"
              />
              <p className="mt-1 text-xs text-slate-600">
                Quello che appare nel "Da:" dell'email. Es. "ENERGIA ARANCIA &lt;contratto@orange-energy.it&gt;"
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
            ℹ️ Con SendGrid attivo, tutti i flussi email (pre-contratti, OTP, marketing, reminder) verranno
            instradati via API. La configurazione SMTP Aruba rimane salvata e puoi tornare ad usarla in
            qualunque momento ri-selezionando "SMTP Aruba" sopra.
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;